import { apiClient } from './api';
import type { DashboardStats, Banner, DiscountCode, PaginatedResponse } from '../types';

type PagePayload<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

function unwrapData<T>(res: { success: boolean; data?: T; message?: string; error?: string }, fallbackMsg: string): T {
  if (!res.success || res.data === undefined) {
    throw new Error(res.message || res.error || fallbackMsg);
  }
  return res.data;
}

export const dashboardService = {
  async getStats(range = 'this_month'): Promise<DashboardStats> {
    const res = await apiClient.get<DashboardStats>('/admin/dashboard/stats', {
      params: { range },
    });
    return unwrapData(res, 'خطا در دریافت آمار');
  },
};

export const bannerService = {
  async list(): Promise<Banner[]> {
    const res = await apiClient.get<Banner[]>('/admin/banners');
    return unwrapData(res, 'خطا در دریافت بنرها');
  },
  async create(data: Record<string, unknown>): Promise<Banner> {
    const res = await apiClient.post<Banner>('/admin/banners', data);
    return unwrapData(res, 'ایجاد بنر ناموفق بود');
  },
  async update(id: string, data: Record<string, unknown>): Promise<Banner> {
    const res = await apiClient.put<Banner>(`/admin/banners/${id}`, data);
    return unwrapData(res, 'ویرایش بنر ناموفق بود');
  },
  async remove(id: string): Promise<void> {
    const res = await apiClient.delete(`/admin/banners/${id}`);
    if (!res.success) throw new Error(res.message || 'حذف بنر ناموفق بود');
  },
};

export const couponService = {
  async list(): Promise<DiscountCode[]> {
    const res = await apiClient.get<DiscountCode[]>('/admin/coupons');
    return unwrapData(res, 'خطا در دریافت تخفیف‌ها');
  },
  async create(data: Record<string, unknown>): Promise<DiscountCode> {
    const res = await apiClient.post<DiscountCode>('/admin/coupons', data);
    return unwrapData(res, 'ایجاد کد تخفیف ناموفق بود');
  },
  async update(id: string, data: Record<string, unknown>): Promise<DiscountCode> {
    const res = await apiClient.put<DiscountCode>(`/admin/coupons/${id}`, data);
    return unwrapData(res, 'ویرایش کد تخفیف ناموفق بود');
  },
  async remove(id: string): Promise<void> {
    const res = await apiClient.delete(`/admin/coupons/${id}`);
    if (!res.success) throw new Error(res.message || 'حذف کد تخفیف ناموفق بود');
  },
};

export const inventoryService = {
  async list(page = 1, limit = 50): Promise<PaginatedResponse<Record<string, unknown>>> {
    const res = await apiClient.get<PagePayload<Record<string, unknown>>>('/admin/inventory', {
      params: { page, limit },
    });
    const data = unwrapData(res, 'خطا در دریافت موجودی');
    return {
      data: data.data ?? [],
      total: data.total ?? 0,
      page: data.page ?? page,
      limit: data.limit ?? limit,
      totalPages: data.totalPages ?? 1,
    };
  },

  async adjust(productId: string, delta: number, reason: string): Promise<void> {
    const res = await apiClient.post(`/admin/inventory/${productId}/adjust`, { delta, reason });
    if (!res.success) throw new Error(res.message || 'تغییر موجودی ناموفق بود');
  },
};
